import React, { ChangeEvent, FC, useEffect, useState } from "react";
import { FormControl, TextField } from "@mui/material";
import {
  QuestionComponentType,
  QuestionConfigDetailDto,
  QuestionConfigTranslationDto,
} from "@green0meter/api-client";

import NumberInput from "./components/NumberInput";
import TextInput from "./components/TextInput";
import RadioInput from "./components/RadioInput";
import Checkbox from "./components/Checkbox";
import DateInput from "./components/DateInput";
import EmailInput from "./components/EmailInput";
import SelectInput from "./components/SelectInput";
import SliderInput from "./components/SliderInput";
import RichTextBoxInput from "./components/RichTextBoxInput";
import MultiInput from "./components/MultiInput";
import TableInput from "./components/TableInput";

import { useQuestionEditorStore } from "@components/main/questions/QuestionEditor";
import { Stack } from "@components/base";

interface QuestionTypeEditor {
  questionConfig: QuestionConfigDetailDto;
  setQuestionConfig: (questionConfig: QuestionConfigDetailDto) => void;
}

const QuestionTypeEditor: FC<QuestionTypeEditor> = ({
  questionConfig,
  setQuestionConfig,
}) => {
  const { componentType } = questionConfig;
  const { language } = useQuestionEditorStore();

  const [translation, setTranslation] = useState<QuestionConfigTranslationDto>();

  const handleChangeName = (event: ChangeEvent<HTMLInputElement>): void => {
    if (questionConfig) {
      setQuestionConfig({
        ...questionConfig,
        translations: questionConfig.translations.map((translation) =>
          translation.language === language
            ? { ...translation, name: event.target.value }
            : translation,
        ),
      });
    }
  };

  useEffect(() => {
    if (questionConfig) {
      const currentTranslation = questionConfig.translations.find(
        (translation) => translation.language === language,
      );

      setTranslation(currentTranslation);
    }
  }, [questionConfig, language]);

  return questionConfig ? (
    <Stack spacing={2}>
      <FormControl>
        <TextField
          value={translation?.name}
          onChange={handleChangeName}
          variant="standard"
          placeholder="Question"
          fullWidth
        />
      </FormControl>
      {componentType === QuestionComponentType.Number && (
        <NumberInput
          questionConfig={questionConfig}
          setQuestionConfig={setQuestionConfig}
        />
      )}
      {componentType === QuestionComponentType.ShortText && (
        <TextInput
          questionConfig={questionConfig}
          setQuestionConfig={setQuestionConfig}
        />
      )}
      {componentType === QuestionComponentType.Radio && (
        <RadioInput
          questionConfig={questionConfig}
          setQuestionConfig={setQuestionConfig}
        />
      )}
      {componentType === QuestionComponentType.Checkbox && (
        <Checkbox
          questionConfig={questionConfig}
          setQuestionConfig={setQuestionConfig}
        />
      )}
      {componentType === QuestionComponentType.DatePicker && (
        <DateInput
          questionConfig={questionConfig}
          setQuestionConfig={setQuestionConfig}
        />
      )}
      {componentType === QuestionComponentType.Email && (
        <EmailInput
          questionConfig={questionConfig}
          setQuestionConfig={setQuestionConfig}
        />
      )}
      {componentType === QuestionComponentType.Select && (
        <SelectInput
          questionConfig={questionConfig}
          setQuestionConfig={setQuestionConfig}
        />
      )}
      {componentType === QuestionComponentType.Slider && <SliderInput />}
      {componentType === QuestionComponentType.RichTextBox && (
        <RichTextBoxInput
          questionConfig={questionConfig}
          setQuestionConfig={setQuestionConfig}
        />
      )}
      {componentType === QuestionComponentType.MultiInput && <MultiInput />}
      {componentType === QuestionComponentType.Table && <TableInput />}
    </Stack>
  ) : null;
};

export default QuestionTypeEditor;
